import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import React from 'react';
import { useIntl } from 'react-intl';
import {
	IChatMediaMessage,
	IFileResource,
	isAttachUiVisible,
	MessageType,
	Resource,
	ShowTypingState,
} from '@enuan/chat__utils';
import { bytesToSI, uglyBytes } from '@avalenti89/typescript-utils';
import { configurationContext } from '../../../contexts/configuration';
import { submitContext } from '../../../contexts/submit.context';
import { attachMessages } from './i18n';

// ── types ────────────────────────────────────────────────────────────

/**
 * Return value of {@link useAttachMedia}.
 *
 * Provides shared helpers for attaching a file (from the modal picker or a
 * clipboard paste) into the submit form, centralising validation logic.
 */
export type UseAttachMediaReturn = {
	/**
	 * Validates and sets `resource` as the pending media message.
	 *
	 * When validation fails (upload disabled, size exceeded, or mime not
	 * in the allowed list) `submitContext.setError` is called with a
	 * human-readable i18n string and the message state is left unchanged.
	 *
	 * @param resource - The resolved `IFileResource` to attach, or `null` to
	 *   indicate the operation was cancelled by the user.
	 * @param onAttached - Optional callback invoked only after the resource has
	 *   been accepted and the message state updated (e.g. close a modal).
	 */
	attachResource: (
		resource: IFileResource | null,
		onAttached?: () => void,
	) => void;

	/**
	 * Convenience wrapper around {@link attachResource}.
	 *
	 * Converts a raw `File` to `IFileResource` via `Resource(file)` and then
	 * delegates to `attachResource`.  Handles `null`/`undefined` as a
	 * no-op (treat as cancelled).
	 *
	 * @param file - The browser `File` to convert and attach, or null/undefined.
	 * @param onAttached - Forwarded verbatim to {@link attachResource}.
	 */
	attachFile: (
		file: File | null | undefined,
		onAttached?: () => void,
	) => Promise<void>;

	/**
	 * Whether file attachment via paste or drag-and-drop is currently permitted.
	 *
	 * `true` when file upload is enabled, the UI-only `attach_ui` flag does not
	 * hide the attach feature, **and** no message is currently pending, so the
	 * user cannot accidentally overwrite work in progress.  Mirrors the
	 * `isAttachDisabled` condition from `useSubmitFormState`.
	 */
	canAttachMedia: boolean;
};

// ── useFileDrop types ─────────────────────────────────────────────────

/**
 * Configuration for {@link useFileDrop}.
 */
export type UseFileDropOptions = {
	/**
	 * When `false` the hook ignores all drag events and never prevents default
	 * browser behaviour (the browser shows the "not-allowed" cursor naturally).
	 */
	enabled: boolean;

	/**
	 * Called with the first dropped `File` once `enabled` is true and the
	 * `dataTransfer` contains at least one file.
	 *
	 * Only the first file is forwarded (consistent with the paste flow).
	 */
	onFile: (file: File) => void;
};

/**
 * Return value of {@link useFileDrop}.
 */
export type UseFileDropReturn = {
	/**
	 * `true` while a drag carrying files is hovering over the target element.
	 * Use this to apply a visual highlight (e.g. a dashed border overlay).
	 */
	isDraggingOver: boolean;

	/**
	 * Spread these handlers on the element that should act as the drop target.
	 *
	 * ```tsx
	 * <div {...dropHandlers}>...</div>
	 * ```
	 */
	dropHandlers: {
		onDragEnter: React.DragEventHandler<HTMLElement>;
		onDragLeave: React.DragEventHandler<HTMLElement>;
		onDragOver: React.DragEventHandler<HTMLElement>;
		onDrop: React.DragEventHandler<HTMLElement>;
	};
};

// ── hook ─────────────────────────────────────────────────────────────

/**
 * Shared hook that encapsulates file attachment validation and state updates
 * for both the `Attach` modal picker and the `Textarea` clipboard paste flow.
 *
 * Reads `file_upload` and `attach_ui` configuration from
 * `configurationContext` and write callbacks from `submitContext`. The hook
 * centralises the rules that were previously duplicated inline in
 * `Attach.tsx`, and gates on both flags: `file_upload.enabled` (backend
 * capability) and `attach_ui` (UI-only visibility, see `isAttachUiVisible`).
 */
export function useAttachMedia(): UseAttachMediaReturn {
	const { file_upload, attach_ui } = useContext(configurationContext);
	const { enabled, max_size, allowed_types } = file_upload ?? {
		enabled: false,
	};
	// UI-only gate: independent from the backend `enabled` flag above, see
	// `attach_ui` in `IConfiguration`.
	const uiVisible = isAttachUiVisible({ attach_ui });

	const { message, setMessage, setError, setComposingState } =
		useContext(submitContext);

	const intl = useIntl();

	// Pre-compute byte limit once per configuration change to avoid repeated
	// string-to-number conversions inside the callback.
	const uglySize = useMemo(() => uglyBytes(max_size ?? ''), [max_size]);
	const readableSize = useMemo(() => bytesToSI(uglySize), [uglySize]);

	const attachResource = useCallback(
		(resource: IFileResource | null, onAttached?: () => void): void => {
			if (!resource) return;

			if (!enabled || !uiVisible) return;

			if (resource.size > uglySize) {
				setError(
					intl.formatMessage(attachMessages.attach_error_size, {
						size: readableSize,
					}),
				);
				return;
			}

			if (
				allowed_types?.length &&
				!allowed_types.includes(resource.mime_type)
			) {
				setError(intl.formatMessage(attachMessages.attach_error_type));
				return;
			}

			const msg: IChatMediaMessage<IFileResource> = {
				_type: MessageType.MEDIA,
				_data: resource,
			};
			setMessage(msg);
			setComposingState(ShowTypingState.PAUSED);
			onAttached?.();
		},
		[
			enabled,
			uiVisible,
			uglySize,
			readableSize,
			allowed_types,
			setMessage,
			setError,
			setComposingState,
			intl,
		],
	);

	const attachFile = useCallback(
		async (
			file: File | null | undefined,
			onAttached?: () => void,
		): Promise<void> => {
			if (!file) return;
			const resource = await Resource(file);
			attachResource(resource, onAttached);
		},
		[attachResource],
	);

	// Mirrors `isAttachDisabled` from `useSubmitFormState`: attachment via paste
	// or drop is blocked when any message is already pending so the user cannot
	// accidentally overwrite work in progress. Also blocked when the UI-only
	// `attach_ui` flag hides the attach feature, independently of `enabled`.
	const canAttachMedia = useMemo(
		() => !!enabled && uiVisible && !message,
		[enabled, uiVisible, message],
	);

	return useMemo(
		() => ({ attachResource, attachFile, canAttachMedia }),
		[attachResource, attachFile, canAttachMedia],
	);
}

// ── useFileDrop ───────────────────────────────────────────────────────

/**
 * Returns event handlers that turn any element into a file drop target.
 *
 * Drag events are filtered so that only drags whose `dataTransfer.types`
 * include `'Files'` are handled — text selections, browser links, and other
 * non-file drags are ignored and never prevent default browser behaviour.
 *
 * `isDraggingOver` is managed with an entry counter (`dragDepth`) rather than
 * a boolean flag.  This prevents the common flicker caused by `dragleave`
 * firing when the pointer moves over a child element inside the drop target.
 *
 * @param options - {@link UseFileDropOptions} configuration.
 */
export function useFileDrop({
	enabled,
	onFile,
}: UseFileDropOptions): UseFileDropReturn {
	const [isDraggingOver, setIsDraggingOver] = useState(false);

	// Counter tracks how many nested dragenter events have fired without a
	// matching dragleave, preventing flickering caused by child elements.
	const dragDepth = useRef(0);

	/** Returns true when the drag event carries at least one file. */
	const hasFiles = (e: React.DragEvent<HTMLElement>) =>
		e.dataTransfer.types.includes('Files');

	const onDragEnter = useCallback(
		(e: React.DragEvent<HTMLElement>) => {
			if (!enabled || !hasFiles(e)) return;
			e.preventDefault();
			dragDepth.current += 1;
			if (dragDepth.current === 1) setIsDraggingOver(true);
		},
		[enabled],
	);

	const onDragLeave = useCallback(
		(e: React.DragEvent<HTMLElement>) => {
			if (!enabled || !hasFiles(e)) return;
			e.preventDefault();
			dragDepth.current -= 1;
			if (dragDepth.current === 0) setIsDraggingOver(false);
		},
		[enabled],
	);

	/**
	 * Must call `preventDefault()` to signal to the browser that the element
	 * accepts drops; without it the `drop` event never fires.
	 */
	const onDragOver = useCallback(
		(e: React.DragEvent<HTMLElement>) => {
			if (!enabled || !hasFiles(e)) return;
			e.preventDefault();
		},
		[enabled],
	);

	const onDrop = useCallback(
		(e: React.DragEvent<HTMLElement>) => {
			// Always prevent the browser from navigating to or opening the dropped
			// file, regardless of whether the upload feature is currently enabled.
			e.preventDefault();
			if (!enabled) return;
			dragDepth.current = 0;
			setIsDraggingOver(false);
			const file = e.dataTransfer.files[0];
			if (file) onFile(file);
		},
		[enabled, onFile],
	);

	const dropHandlers = useMemo(
		() => ({ onDragEnter, onDragLeave, onDragOver, onDrop }),
		[onDragEnter, onDragLeave, onDragOver, onDrop],
	);

	return useMemo(
		() => ({ isDraggingOver, dropHandlers }),
		[isDraggingOver, dropHandlers],
	);
}

// ── useFileDrag types ──────────────────────────────────────────────────

/**
 * Return value of {@link useFileDrag}.
 */
export type UseFileDragReturn = {
	/**
	 * `true` while a file drag is active over the tracked element.
	 * Publish this via {@link FileDragContext} so that descendant components
	 * (e.g. the footer overlay) react to the frame-level drag state.
	 */
	isDraggingOver: boolean;

	/**
	 * Spread these handlers on the element that should detect drags.
	 *
	 * Unlike {@link UseFileDropReturn.dropHandlers}, these handlers do NOT
	 * process the dropped file — they only track entry/leave depth so that
	 * `isDraggingOver` stays accurate.  The `onDrop` handler calls
	 * `preventDefault` to prevent the browser from navigating to the file
	 * when the user releases outside the actual drop target.
	 *
	 * ```tsx
	 * <section {...dragHandlers}>...</section>
	 * ```
	 */
	dragHandlers: {
		onDragEnter: React.DragEventHandler<HTMLElement>;
		onDragLeave: React.DragEventHandler<HTMLElement>;
		onDragOver: React.DragEventHandler<HTMLElement>;
		onDrop: React.DragEventHandler<HTMLElement>;
	};
};

// ── useFileDrag ────────────────────────────────────────────────────────

/**
 * Detects whether a file drag is active over the target element without
 * acting as a drop target itself.
 *
 * Use this at a high level in the component tree (e.g. `ChatMessages`) to
 * obtain a frame-wide `isDraggingOver` flag.  Provide that flag via
 * {@link FileDragContext} so descendants can show a drop-zone overlay without
 * needing the pointer to be directly over the drop target element.
 *
 * The `onDrop` handler on the detection element calls `preventDefault` to
 * prevent the browser from opening/navigating to files dropped outside the
 * real drop target (`FormWithDrop`).
 *
 * @param options.enabled - When `false` the hook is a no-op and all handlers
 *   are inert.
 */
export function useFileDrag({
	enabled,
}: {
	enabled: boolean;
}): UseFileDragReturn {
	const [isDraggingOver, setIsDraggingOver] = useState(false);

	// Depth counter prevents flickering when the pointer moves over child
	// elements (dragleave on parent fires before dragenter on child).
	const dragDepth = useRef(0);

	/** Returns `true` when the drag event carries at least one file. */
	const hasFiles = (e: React.DragEvent<HTMLElement>) =>
		e.dataTransfer.types.includes('Files');

	const onDragEnter = useCallback(
		(e: React.DragEvent<HTMLElement>) => {
			if (!enabled || !hasFiles(e)) return;
			e.preventDefault();
			dragDepth.current += 1;
			if (dragDepth.current === 1) setIsDraggingOver(true);
		},
		[enabled],
	);

	const onDragLeave = useCallback(
		(e: React.DragEvent<HTMLElement>) => {
			if (!enabled || !hasFiles(e)) return;
			dragDepth.current -= 1;
			if (dragDepth.current === 0) setIsDraggingOver(false);
		},
		[enabled],
	);

	/**
	 * Must call `preventDefault` to signal that this element accepts drags so
	 * the browser renders the correct drag cursor while over the frame.
	 */
	const onDragOver = useCallback(
		(e: React.DragEvent<HTMLElement>) => {
			if (!enabled || !hasFiles(e)) return;
			e.preventDefault();
		},
		[enabled],
	);

	/**
	 * Prevents the browser from opening the file when the user releases the
	 * drag outside the actual drop target (the `<form>` inside `FormWithDrop`).
	 * Does not process the file — that is handled by `useFileDrop`.
	 */
	const onDrop = useCallback((e: React.DragEvent<HTMLElement>) => {
		if (!hasFiles(e)) return;
		// Prevent browser navigation/file-open for drops outside the form.
		e.preventDefault();
		dragDepth.current = 0;
		setIsDraggingOver(false);
	}, []);

	const dragHandlers = useMemo(
		() => ({ onDragEnter, onDragLeave, onDragOver, onDrop }),
		[onDragEnter, onDragLeave, onDragOver, onDrop],
	);

	return useMemo(
		() => ({ isDraggingOver, dragHandlers }),
		[isDraggingOver, dragHandlers],
	);
}
